import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Header,
  Button,
  Message,
  Grid,
  Card,
  Icon,
  Label,
  Divider,
  Dropdown,
  Tab,
  Table,
  Modal,
  TextArea,
  Statistic
} from 'semantic-ui-react';
import UniversalMessageIcon from './UniversalMessageIcon';
import { useMessageContext } from './MessageProvider';
import { sendMessage } from '../../slices/messageSlice';
import { selectCurrentUser } from '../../slices/userSlice';
import {
  MESSAGE_TEMPLATES,
  RATE_CALCULATIONS
} from './models/BusinessMessageModels';
import { MESSAGE_TYPES } from '../../constants/messageConstants';
import testConfigManager from './utils/TestConfigManager';
import VoiceActorSelector from './components/VoiceActorSelector';
import BookCreatorSelector from './components/BookCreatorSelector';
import AdminOverrideSelector from './components/AdminOverrideSelector';
import ActiveTestContext from './components/ActiveTestContext';
import MessageVerificationModal from './components/MessageVerificationModal';

/**
 * Enhanced Test component for Universal Messaging System
 * Features persistent configuration, realistic message templates, and user switching
 */
const UniversalMessagingTest = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const {
    userRole,
    unreadCounts,
    businessMessageTypes,
    isConnected,
    canSendMessage,
    canReceiveMessage,
    getAvailableMessageTypes,
    businessMessageConfig
  } = useMessageContext();

  // Production-mirroring test harness state
  const [testHarnessState, setTestHarnessState] = useState({
    // Sequential workflow phase
    phase: 'SELECT_PARTICIPANTS', // SELECT_PARTICIPANTS | SELECT_SCENARIO | VALIDATE | READY | INITIALIZED

    // Production state simulation (mirrors real system)
    currentUser: null,           // Simulates authenticated user
    recipientId: null,          // Simulates selected recipient  
    opportunityId: null,        // Simulates business context
    messageType: '',            // Real message type from production
    messageContent: '',         // Real message content

    // Test harness participant selection (UI helpers)
    participants: {
      voiceActorId: null,
      bookCreatorId: null,
      adminId: null,
      isAdminOverride: false
    },

    // Scenario selection and validation (UI helpers)
    scenarioId: null,
    scenario: null,
    isScenarioValid: false,
    validationMessage: '',

    // Message composer state (mirrors production)
    isMessageReady: false
  });

  // Data loading and management
  const [userProfiles, setUserProfiles] = useState({});
  const [opportunities, setOpportunities] = useState({});
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [settings, setSettings] = useState({});

  // UI state management
  const [isLoading, setIsLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showScenariosModal, setShowScenariosModal] = useState(false);
  const [useRealData, setUseRealData] = useState(false);
  const [isLoadingRealData, setIsLoadingRealData] = useState(false);
  const [realDataError, setRealDataError] = useState(null);
  const [realDataStatus, setRealDataStatus] = useState('mock');

  // Categorized user profiles (derived from userProfiles)
  const [voiceActorProfiles, setVoiceActorProfiles] = useState({});
  const [bookCreatorProfiles, setBookCreatorProfiles] = useState({});
  const [adminProfiles, setAdminProfiles] = useState({});

  // Message verification state management
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [messageDeliveryStatus, setMessageDeliveryStatus] = useState('sent');

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
    loadBusinessScenarios();
  }, []);

  // Load business scenarios
  const loadBusinessScenarios = () => {
    const scenarios = testConfigManager.getBusinessMessageScenarios();
    setAvailableScenarios(scenarios);
  };

  // Load persistent configuration with enhanced error handling and logging
  const loadConfiguration = async (forceRealData = null) => {
    const dataMode = forceRealData !== null ? forceRealData : useRealData;

    try {
      console.log(`[TestLab] Loading configuration in ${dataMode ? 'REAL' : 'MOCK'} data mode`);

      // Clear previous errors
      setRealDataError(null);

      // Set loading status
      if (dataMode) {
        setRealDataStatus('loading');
        setIsLoadingRealData(true);
      }

      // Load data with proper error handling
      const profiles = await testConfigManager.getUserProfiles(dataMode, currentUser);
      const opps = await testConfigManager.getOpportunities(dataMode);
      const activeUserData = testConfigManager.getActiveUser();
      const history = testConfigManager.getConversationHistory();
      const currentSettings = testConfigManager.getSettings();

      // Validate loaded data
      if (!profiles || Object.keys(profiles).length === 0) {
        throw new Error('No user profiles loaded. Check API connectivity and user permissions.');
      }

      console.log(`[TestLab] Successfully loaded ${Object.keys(profiles).length} user profiles`);
      console.log(`[TestLab] Successfully loaded ${Object.keys(opps || {}).length} opportunities`);

      // Update state with categorized users
      setUserProfiles(profiles);
      setOpportunities(opps || {});
      setConversationHistory(history);
      setSettings(currentSettings);

      // Handle categorized users (new architecture)
      if (profiles.all && typeof profiles.all === 'object') {
        // New categorized format
        setVoiceActorProfiles(profiles.voiceActors || {});
        setBookCreatorProfiles(profiles.bookCreators || {});
        setAdminProfiles(profiles.admins || {});

        console.log(`[TestLab] Loaded categorized users:`);
        console.log(`- Voice Actors: ${Object.keys(profiles.voiceActors || {}).length}`);
        console.log(`- Book Creators: ${Object.keys(profiles.bookCreators || {}).length}`);
        console.log(`- Admins: ${Object.keys(profiles.admins || {}).length}`);
      } else {
        // Flat format - categorize manually for backward compatibility
        const voiceActorUsers = {};
        const bookCreatorUsers = {};
        const adminUsers = {};

        Object.entries(profiles).forEach(([key, user]) => {
          if (user.role === 'VOICE_ACTOR') voiceActorUsers[key] = user;
          if (user.role === 'BOOK_CREATOR') bookCreatorUsers[key] = user;
          if (user.role === 'ADMIN') adminUsers[key] = user;
        });

        setVoiceActorProfiles(voiceActorUsers);
        setBookCreatorProfiles(bookCreatorUsers);
        setAdminProfiles(adminUsers);
      }

      // Clear test harness selections when switching modes
      setTestHarnessState(prev => ({
        ...prev,
        phase: 'SELECT_PARTICIPANTS',
        currentUser: null,
        recipientId: null,
        opportunityId: null,
        messageType: '',
        messageContent: '',
        participants: {
          voiceActorId: null,
          bookCreatorId: null,
          adminId: null,
          isAdminOverride: false
        },
        scenarioId: null,
        scenario: null,
        isScenarioValid: false,
        validationMessage: '',
        isMessageReady: false
      }));

      // Handle active user validation when switching modes (legacy support)
      const allUsers = profiles.all || profiles;
      if (activeUserData && allUsers[activeUserData.userId]) {
        setTestHarnessState(prev => ({
          ...prev,
          currentUser: allUsers[activeUserData.userId]
        }));
      } else if (activeUserData && !allUsers[activeUserData.userId]) {
        // Active user not found in new data set, clear selection
        console.log(`[TestLab] Active user ${activeUserData.userId} not found in ${dataMode ? 'real' : 'mock'} data, clearing selection`);
        testConfigManager.setActiveUser(null);
      }

      // Update status
      setRealDataStatus(dataMode ? 'real' : 'mock');
      setIsLoadingRealData(false);

      const totalUserCount = profiles.all ? Object.keys(profiles.all).length : Object.keys(profiles).length;
      addTestResult('success', `Configuration loaded successfully in ${dataMode ? 'REAL' : 'MOCK'} data mode`, {
        userCount: totalUserCount,
        opportunityCount: Object.keys(opps || {}).length,
        dataMode: dataMode ? 'real' : 'mock'
      });

    } catch (error) {
      console.error('[TestLab] Failed to load configuration:', error);

      setRealDataError(error.message);
      setRealDataStatus('error');
      setIsLoadingRealData(false);

      // If real data failed, try to fallback to mock data
      if (dataMode) {
        console.log('[TestLab] Real data failed, attempting fallback to mock data');
        addTestResult('warning', `Real data loading failed: ${error.message}. Falling back to mock data.`);

        try {
          const mockProfiles = await testConfigManager.getUserProfiles(false);
          const mockOpps = await testConfigManager.getOpportunities(false);

          setUserProfiles(mockProfiles);
          setOpportunities(mockOpps || {});
          setRealDataStatus('mock');
          setUseRealData(false);

          addTestResult('info', 'Successfully fell back to mock data');
        } catch (fallbackError) {
          console.error('[TestLab] Fallback to mock data also failed:', fallbackError);
          addTestResult('error', `Both real and mock data loading failed: ${fallbackError.message}`);
        }
      } else {
        addTestResult('error', `Failed to load configuration: ${error.message}`);
      }
    }
  };

  // Toggle real data usage with enhanced handling
  const handleToggleRealData = async () => {
    const newDataMode = !useRealData;

    console.log(`[TestLab] Toggling data mode from ${useRealData ? 'REAL' : 'MOCK'} to ${newDataMode ? 'REAL' : 'MOCK'}`);

    // Clear test harness state when switching modes
    setTestHarnessState(prev => ({
      ...prev,
      phase: 'SELECT_PARTICIPANTS',
      currentUser: null,
      recipientId: null,
      opportunityId: null,
      messageType: '',
      messageContent: '',
      participants: {
        voiceActorId: null,
        bookCreatorId: null,
        adminId: null,
        isAdminOverride: false
      },
      scenarioId: null,
      scenario: null,
      isScenarioValid: false,
      validationMessage: '',
      isMessageReady: false
    }));

    // Clear any previous errors
    setRealDataError(null);

    // Update the toggle state immediately for UI responsiveness
    setUseRealData(newDataMode);

    // Load configuration with the new data mode
    await loadConfiguration(newDataMode);
  };

  // New architecture handlers with enhanced integration
  const handleVoiceActorChange = (voiceActorId) => {
    setTestHarnessState(prev => ({
      ...prev,
      participants: {
        ...prev.participants,
        voiceActorId: voiceActorId
      }
    }));

    if (voiceActorId && voiceActorProfiles[voiceActorId]) {
      // Auto-set as active user if not in admin override mode
      if (!testHarnessState.participants.isAdminOverride) {
        setTestHarnessState(prev => ({
          ...prev,
          currentUser: voiceActorProfiles[voiceActorId]
        }));
        testConfigManager.setActiveUser(voiceActorId);

        // Clear message composer state
        setTestHarnessState(prev => ({
          ...prev,
          messageType: '',
          messageContent: '',
          recipientId: null,
          opportunityId: null,
          isMessageReady: false
        }));
      }

      addTestResult('info', `Selected Voice Actor: ${voiceActorProfiles[voiceActorId].name}${!testHarnessState.participants.isAdminOverride ? ' (set as active user)' : ''}`);
    }
  };

  const handleBookCreatorChange = (bookCreatorId) => {
    setTestHarnessState(prev => ({
      ...prev,
      participants: {
        ...prev.participants,
        bookCreatorId: bookCreatorId
      }
    }));

    if (bookCreatorId && bookCreatorProfiles[bookCreatorId]) {
      // Auto-set as active user if not in admin override mode
      if (!testHarnessState.participants.isAdminOverride) {
        setTestHarnessState(prev => ({
          ...prev,
          currentUser: bookCreatorProfiles[bookCreatorId]
        }));
        testConfigManager.setActiveUser(bookCreatorId);

        // Clear message composer state
        setTestHarnessState(prev => ({
          ...prev,
          messageType: '',
          messageContent: '',
          recipientId: null,
          opportunityId: null,
          isMessageReady: false
        }));
      }

      addTestResult('info', `Selected Book Creator: ${bookCreatorProfiles[bookCreatorId].name}${!testHarnessState.participants.isAdminOverride ? ' (set as active user)' : ''}`);
    }
  };

  const handleAdminChange = (adminId) => {
    setTestHarnessState(prev => ({
      ...prev,
      participants: {
        ...prev.participants,
        adminId: adminId
      }
    }));

    if (adminId && adminProfiles[adminId]) {
      // Auto-set as active user when admin override is enabled
      if (testHarnessState.participants.isAdminOverride) {
        setTestHarnessState(prev => ({
          ...prev,
          currentUser: adminProfiles[adminId]
        }));
        testConfigManager.setActiveUser(adminId);

        // Clear message composer state
        setTestHarnessState(prev => ({
          ...prev,
          messageType: '',
          messageContent: '',
          recipientId: null,
          opportunityId: null,
          isMessageReady: false
        }));
      }

      addTestResult('info', `Selected Admin: ${adminProfiles[adminId].name}${testHarnessState.participants.isAdminOverride ? ' (set as active user)' : ''}`);
    }
  };

  const handleToggleAdminOverride = () => {
    const newOverrideState = !testHarnessState.participants.isAdminOverride;

    setTestHarnessState(prev => ({
      ...prev,
      participants: {
        ...prev.participants,
        isAdminOverride: newOverrideState
      }
    }));

    if (newOverrideState && testHarnessState.participants.adminId) {
      addTestResult('warning', `Admin Override ENABLED for ${adminProfiles[testHarnessState.participants.adminId].name}. Can now test any scenario combination.`);
    } else {
      addTestResult('info', 'Admin Override DISABLED. Normal role restrictions apply.');
    }
  };

  // Run business scenario with sequential workflow
  const runBusinessScenario = async (scenarioId) => {
    if (!scenarioId) return;

    setIsLoading(true);
    try {
      const data = await testConfigManager.generateScenarioData(scenarioId, useRealData);

      // Validate that the scenario has a valid message type
      if (!data.scenario.messageType) {
        throw new Error(`Scenario ${scenarioId} has no message type defined`);
      }

      // Validate that the message type exists in the system
      const availableTypes = getAvailableMessageTypesForUser();
      const messageTypeExists = availableTypes.some(type => type.value === data.scenario.messageType);

      if (!messageTypeExists && testHarnessState.currentUser) {
        console.warn(`[TestLab] Message type ${data.scenario.messageType} not available for user role ${testHarnessState.currentUser.role}`);
        addTestResult('warning', `Message type ${data.scenario.messageType} may not be available for current user role. Proceeding with scenario load.`);
      }

      // Determine sender and recipient based on message type business rules
      let senderUser = null;
      let recipientUser = null;

      // Apply message type-specific business rules for sender/recipient assignment
      if (data.scenario.messageType === MESSAGE_TYPES.VOICE_ACTOR_APPLICATION) {
        // Voice Actor applies to Book Creator
        if (testHarnessState.participants.voiceActorId && voiceActorProfiles[testHarnessState.participants.voiceActorId]) {
          senderUser = voiceActorProfiles[testHarnessState.participants.voiceActorId];
        } else {
          senderUser = Object.values(voiceActorProfiles)[0] || data.sender;
        }

        if (testHarnessState.participants.bookCreatorId && bookCreatorProfiles[testHarnessState.participants.bookCreatorId]) {
          recipientUser = bookCreatorProfiles[testHarnessState.participants.bookCreatorId];
        } else {
          recipientUser = Object.values(bookCreatorProfiles)[0] || data.recipient;
        }
      } else if (data.scenario.messageType === MESSAGE_TYPES.APPLICATION_STATUS_UPDATE) {
        // Book Creator responds to Voice Actor - FIXED: Ensure book creator is sender
        if (testHarnessState.participants.bookCreatorId && bookCreatorProfiles[testHarnessState.participants.bookCreatorId]) {
          senderUser = bookCreatorProfiles[testHarnessState.participants.bookCreatorId];
        } else {
          senderUser = Object.values(bookCreatorProfiles)[0] || data.sender;
        }

        if (testHarnessState.participants.voiceActorId && voiceActorProfiles[testHarnessState.participants.voiceActorId]) {
          recipientUser = voiceActorProfiles[testHarnessState.participants.voiceActorId];
        } else {
          recipientUser = Object.values(voiceActorProfiles)[0] || data.recipient;
        }
      } else if (data.scenario.messageType === 'FINANCIAL_TRANSACTION') {
        // Book Creator sends payment notifications to Voice Actor
        if (testHarnessState.participants.bookCreatorId && bookCreatorProfiles[testHarnessState.participants.bookCreatorId]) {
          senderUser = bookCreatorProfiles[testHarnessState.participants.bookCreatorId];
        } else {
          senderUser = Object.values(bookCreatorProfiles)[0] || data.sender;
        }

        if (testHarnessState.participants.voiceActorId && voiceActorProfiles[testHarnessState.participants.voiceActorId]) {
          recipientUser = voiceActorProfiles[testHarnessState.participants.voiceActorId];
        } else {
          recipientUser = Object.values(voiceActorProfiles)[0] || data.recipient;
        }
      } else if (data.scenario.messageType === 'PROJECT_MILESTONE') {
        // Voice Actor sends progress updates to Book Creator
        if (testHarnessState.participants.voiceActorId && voiceActorProfiles[testHarnessState.participants.voiceActorId]) {
          senderUser = voiceActorProfiles[testHarnessState.participants.voiceActorId];
        } else {
          senderUser = Object.values(voiceActorProfiles)[0] || data.sender;
        }

        if (testHarnessState.participants.bookCreatorId && bookCreatorProfiles[testHarnessState.participants.bookCreatorId]) {
          recipientUser = bookCreatorProfiles[testHarnessState.participants.bookCreatorId];
        } else {
          recipientUser = Object.values(bookCreatorProfiles)[0] || data.recipient;
        }
      } else {
        // Generic fallback for other message types
        if (testHarnessState.participants.isAdminOverride && testHarnessState.participants.adminId && adminProfiles[testHarnessState.participants.adminId]) {
          senderUser = adminProfiles[testHarnessState.participants.adminId];
        } else if (testHarnessState.participants.voiceActorId && voiceActorProfiles[testHarnessState.participants.voiceActorId]) {
          senderUser = voiceActorProfiles[testHarnessState.participants.voiceActorId];
        } else if (testHarnessState.participants.bookCreatorId && bookCreatorProfiles[testHarnessState.participants.bookCreatorId]) {
          senderUser = bookCreatorProfiles[testHarnessState.participants.bookCreatorId];
        } else {
          senderUser = data.sender;
        }
        recipientUser = data.recipient;
      }

      // Validate recipient was found
      if (!recipientUser) {
        console.warn(`[TestLab] No valid recipient found for message type ${data.scenario.messageType}`);
        addTestResult('warning', `No valid recipient found for message type ${data.scenario.messageType}. You may need to select one manually.`);
      }

      // Update test harness state with scenario data
      setTestHarnessState(prev => ({
        ...prev,
        phase: 'READY',
        currentUser: senderUser,
        recipientId: recipientUser?.userId,
        opportunityId: data.opportunity?.id,
        messageType: data.scenario.messageType,
        messageContent: '', // Will be auto-generated below
        scenarioId: scenarioId,
        scenario: data.scenario,
        isScenarioValid: true,
        validationMessage: `Scenario loaded: ${data.scenario.name}`,
        isMessageReady: false
      }));

      // Set active user in config manager
      testConfigManager.setActiveUser(senderUser.userId);

      // Auto-generate message content for the loaded scenario
      // We need to wait for state to update, so use setTimeout to ensure state is available
      setTimeout(() => {
        try {
          // Create temporary context for content generation
          const tempContext = {
            messageType: data.scenario.messageType,
            currentUser: senderUser,
            recipientId: recipientUser?.userId,
            opportunityId: data.opportunity?.id
          };

          const generatedContent = generateMessageContentWithContext(tempContext);
          if (generatedContent && generatedContent.trim()) {
            setTestHarnessState(prev => ({
              ...prev,
              messageContent: generatedContent,
              isMessageReady: true
            }));

            addTestResult('success', `Auto-generated message content for ${data.scenario.messageType}`, {
              contentLength: generatedContent.length,
              messageType: data.scenario.messageType
            });
          }
        } catch (contentError) {
          console.warn('[TestLab] Failed to auto-generate message content:', contentError);
          addTestResult('warning', `Scenario loaded but message content generation failed: ${contentError.message}. Use "Generate Realistic Content" button manually.`);
        }
      }, 100);

      // Log successful initialization
      console.log(`[TestLab] Scenario loaded successfully:`, {
        messageType: data.scenario.messageType,
        sender: senderUser.name,
        recipient: recipientUser?.name || 'None',
        opportunity: data.opportunity?.title || 'None'
      });

      addTestResult('success', `Loaded scenario: ${data.scenario.name} with selected participants`, {
        scenario: data.scenario.description,
        sender: senderUser.name,
        recipient: recipientUser?.name || 'None',
        messageType: data.scenario.messageType,
        usedSelectedParticipants: !!(testHarnessState.participants.voiceActorId || testHarnessState.participants.bookCreatorId || (testHarnessState.participants.isAdminOverride && testHarnessState.participants.adminId))
      });

    } catch (error) {
      console.error('[TestLab] Failed to run scenario:', error);
      addTestResult('error', `Failed to run scenario: ${error.message}`);

      // Clear scenario data on error
      setTestHarnessState(prev => ({
        ...prev,
        scenarioId: null,
        scenario: null,
        isScenarioValid: false,
        validationMessage: `Failed to load scenario: ${error.message}`,
        phase: 'SELECT_SCENARIO'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Generate realistic message content with proper sender context
  const generateMessageContent = () => {
    if (!testHarnessState.messageType || !testHarnessState.currentUser) return '';

    const templates = MESSAGE_TEMPLATES[testHarnessState.messageType];
    if (!templates) return '';

    const templateKey = Object.keys(templates)[0]; // Use first template
    const template = templates[templateKey];

    if (!template || !template.template) return '';

    try {
      // Get relevant opportunity and recipient
      const opportunity = opportunities[testHarnessState.opportunityId] || Object.values(opportunities)[0];
      const recipient = userProfiles[testHarnessState.recipientId];

      // Enhanced sender context to fix signator issue
      const senderContext = {
        ...testHarnessState.currentUser,
        senderName: testHarnessState.currentUser.name,
        senderRole: testHarnessState.currentUser.role,
        senderUserId: testHarnessState.currentUser.userId,
        timestamp: new Date().toISOString()
      };

      if (testHarnessState.messageType === 'VOICE_ACTOR_APPLICATION' && opportunity) {
        const recipient = userProfiles[testHarnessState.recipientId];
        const messageData = template.template(senderContext, opportunity, {
          recipient: recipient,
          explicitSender: testHarnessState.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      if (testHarnessState.messageType === 'APPLICATION_STATUS_UPDATE' && opportunity && recipient) {
        const negotiatedRate = RATE_CALCULATIONS.calculateRate(recipient.baseRate || 500, {
          accents: opportunity.characterRequirements?.accentsRequired?.length > 0,
          multipleAgeRanges: opportunity.characterRequirements?.ageRanges?.length > 1,
          complexEmotional: opportunity.characterRequirements?.emotionalRange === 'COMPLEX',
          exclusivity: opportunity.compensation?.exclusivityRequired,
          characterCount: opportunity.characterRequirements?.totalUniqueVoices || 1
        });
        const messageData = template.template(senderContext, recipient, opportunity, negotiatedRate, {
          explicitSender: testHarnessState.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      if (testHarnessState.messageType === 'FINANCIAL_TRANSACTION') {
        const project = { title: opportunity?.title || 'Sample Project' };
        const milestone = {
          description: 'First quarter completion',
          chapters: ['1', '2', '3', '4'],
          qualityRating: 4.7,
          retakeRate: 0.03,
          pronunciationAccuracy: 0.97,
          emotionalConsistency: 4.8,
          qualityBonus: 150
        };
        const amount = 2500;
        const messageData = template.template(project, milestone, amount, {
          sender: senderContext,
          explicitSender: testHarnessState.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      if (testHarnessState.messageType === 'PROJECT_MILESTONE') {
        const project = {
          title: opportunity?.title || 'Sample Project',
          id: opportunity?.id || 'proj-001',
          estimatedHours: opportunity?.estimatedFinishedHours || 12
        };
        const progress = {
          percentage: 25,
          chaptersCompleted: ['1', '2', '3'],
          hoursRecorded: 3,
          retakeRate: 0.05,
          characterConsistency: 4.6,
          emotionalDelivery: 4.8,
          upcomingChapters: ['4', '5', '6'],
          estimatedCompletion: '2025-11-15',
          onSchedule: true,
          specialChallenges: ['Complex battle scene', 'Multiple character dialogue']
        };
        const messageData = template.template(project, progress, {
          sender: senderContext,
          explicitSender: testHarnessState.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      return 'Generated message content would appear here based on selected template.';
    } catch (error) {
      console.error('Error generating message content:', error);
      console.error('Active user context:', testHarnessState.currentUser);
      return 'Error generating message content. Please check template configuration.';
    }
  };

  // Helper function to generate message content with custom context (for auto-generation)
  const generateMessageContentWithContext = (context) => {
    if (!context.messageType || !context.currentUser) return '';

    const templates = MESSAGE_TEMPLATES[context.messageType];
    if (!templates) return '';

    const templateKey = Object.keys(templates)[0]; // Use first template
    const template = templates[templateKey];

    if (!template || !template.template) return '';

    try {
      // Get relevant opportunity and recipient using context
      const opportunity = opportunities[context.opportunityId] || Object.values(opportunities)[0];
      const allUsers = userProfiles.all || userProfiles;
      const recipient = Object.values(allUsers).find(profile => profile.userId === context.recipientId);

      // Enhanced sender context to fix signator issue
      const senderContext = {
        ...context.currentUser,
        senderName: context.currentUser.name,
        senderRole: context.currentUser.role,
        senderUserId: context.currentUser.userId,
        timestamp: new Date().toISOString()
      };

      if (context.messageType === 'VOICE_ACTOR_APPLICATION' && opportunity) {
        const messageData = template.template(senderContext, opportunity, {
          recipient: recipient,
          explicitSender: context.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      if (context.messageType === 'APPLICATION_STATUS_UPDATE' && opportunity && recipient) {
        const negotiatedRate = RATE_CALCULATIONS.calculateRate(recipient.baseRate || 500, {
          accents: opportunity.characterRequirements?.accentsRequired?.length > 0,
          multipleAgeRanges: opportunity.characterRequirements?.ageRanges?.length > 1,
          complexEmotional: opportunity.characterRequirements?.emotionalRange === 'COMPLEX',
          exclusivity: opportunity.compensation?.exclusivityRequired,
          characterCount: opportunity.characterRequirements?.totalUniqueVoices || 1
        });
        const messageData = template.template(senderContext, recipient, opportunity, negotiatedRate, {
          explicitSender: context.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      if (context.messageType === 'FINANCIAL_TRANSACTION') {
        const project = { title: opportunity?.title || 'Sample Project' };
        const milestone = {
          description: 'First quarter completion',
          chapters: ['1', '2', '3', '4'],
          qualityRating: 4.7,
          retakeRate: 0.03,
          pronunciationAccuracy: 0.97,
          emotionalConsistency: 4.8,
          qualityBonus: 150
        };
        const amount = 2500;
        const messageData = template.template(project, milestone, amount, {
          sender: senderContext,
          explicitSender: context.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      if (context.messageType === 'PROJECT_MILESTONE') {
        const project = {
          title: opportunity?.title || 'Sample Project',
          id: opportunity?.id || 'proj-001',
          estimatedHours: opportunity?.estimatedFinishedHours || 12
        };
        const progress = {
          percentage: 25,
          chaptersCompleted: ['1', '2', '3'],
          hoursRecorded: 3,
          retakeRate: 0.05,
          characterConsistency: 4.6,
          emotionalDelivery: 4.8,
          upcomingChapters: ['4', '5', '6'],
          estimatedCompletion: '2025-11-15',
          onSchedule: true,
          specialChallenges: ['Complex battle scene', 'Multiple character dialogue']
        };
        const messageData = template.template(project, progress, {
          sender: senderContext,
          explicitSender: context.currentUser.name,
          senderContext: senderContext
        });
        return messageData.content;
      }

      return 'Generated message content would appear here based on selected template.';
    } catch (error) {
      console.error('Error generating message content with context:', error);
      console.error('Context provided:', context);
      throw error; // Re-throw to be caught by the calling function
    }
  };

  // Send test message with verification modal and enhanced error handling
  const handleSendTestMessage = async () => {
    // Enhanced validation with user feedback
    if (!testHarnessState.messageType) {
      addTestResult('warning', 'Please select a message type before sending');
      return;
    }

    if (!testHarnessState.recipientId) {
      addTestResult('warning', 'Please select a recipient before sending');
      return;
    }

    if (!testHarnessState.currentUser) {
      addTestResult('warning', 'Please select an active user before sending');
      return;
    }

    const messageContent = testHarnessState.messageContent || generateMessageContent();
    if (!messageContent.trim()) {
      addTestResult('warning', 'Message content cannot be empty. Please enter content or generate it using the template button.');
      return;
    }

    // Validate recipient exists in current user profiles
    const allUsers = userProfiles.all || userProfiles;
    const recipientProfile = Object.values(allUsers).find(profile => profile.userId === testHarnessState.recipientId);
    if (!recipientProfile) {
      addTestResult('error', 'Selected recipient is no longer available. Please refresh and try again.');
      return;
    }

    setIsLoading(true);
    try {
      const messageData = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        senderId: testHarnessState.currentUser.userId,
        recipientId: testHarnessState.recipientId,
        content: messageContent,
        messageType: testHarnessState.messageType,
        senderRole: testHarnessState.currentUser.role,
        metadata: {
          testMessage: true,
          timestamp: new Date().toISOString(),
          opportunityId: testHarnessState.opportunityId,
          voiceActorId: testHarnessState.participants.voiceActorId,
          bookCreatorId: testHarnessState.participants.bookCreatorId,
          adminOverride: testHarnessState.participants.isAdminOverride,
          dataMode: useRealData ? 'real' : 'mock'
        }
      };

      // Validate message data before processing
      if (messageContent.length > 10000) {
        addTestResult('warning', 'Message content is very long. Consider shortening for better readability.');
      }

      // Add to conversation history with error handling
      let savedMessage;
      try {
        savedMessage = testConfigManager.addToConversationHistory(messageData);
      } catch (historyError) {
        console.warn('Failed to save to conversation history:', historyError);
        savedMessage = messageData; // Use original data as fallback
        addTestResult('warning', 'Message sent but not saved to history due to storage limitations');
      }

      // Update local history
      setConversationHistory(prev => [savedMessage, ...prev.slice(0, 99)]);

      // Simulate sending via Redux (in real app, this would go to server)
      await dispatch(sendMessage(messageData));

      // Set up verification data and show modal
      setVerificationData({
        messageData: savedMessage,
        sender: testHarnessState.currentUser,
        recipient: recipientProfile
      });
      setMessageDeliveryStatus('sent');
      setShowVerificationModal(true);

      // Use the already-declared recipientProfile for recipient name lookup
      const recipientName = recipientProfile?.name || 'Unknown Recipient';
      addTestResult('success', `Successfully sent ${testHarnessState.messageType} message from ${testHarnessState.currentUser.name} to ${recipientName}`, {
        messageId: savedMessage.id,
        contentLength: messageContent.length,
        hasOpportunity: !!testHarnessState.opportunityId
      });

      // Update test harness state to indicate message was sent
      setTestHarnessState(prev => ({
        ...prev,
        phase: 'INITIALIZED',
        isMessageReady: true
      }));

    } catch (error) {
      console.error('Failed to send test message:', error);
      addTestResult('error', `Failed to send message: ${error.message}`, {
        errorType: error.name,
        messageType: testHarnessState.messageType,
        sender: testHarnessState.currentUser.name,
        recipient: userProfiles[testHarnessState.recipientId]?.name
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle notification simulation
  const handleSimulateNotification = (messageData, recipient) => {
    addTestResult('info', `Simulated notification for ${recipient.name}`, {
      messageType: messageData.messageType,
      recipient: recipient.name,
      notificationMethod: 'Universal Message Icon + Browser Notification'
    });

    // Simulate delivery status update
    setTimeout(() => {
      setMessageDeliveryStatus('delivered');
      addTestResult('success', `Message delivered to ${recipient.name}`);
    }, 1000);
  };

  // Handle message actions from verification modal (Reply, Forward, Archive)
  const handleMessageAction = (action, data) => {
    switch (action) {
      case 'reply':
        // Add reply message to conversation history
        try {
          const savedReply = testConfigManager.addToConversationHistory(data);
          setConversationHistory(prev => [savedReply, ...prev.slice(0, 99)]);

          addTestResult('success', `Reply sent from ${data.senderRole} to original sender`, {
            messageId: data.id,
            replyTo: data.metadata.replyTo,
            contentLength: data.content.length
          });
        } catch (error) {
          console.error('Failed to save reply to history:', error);
          addTestResult('warning', 'Reply sent but not saved to history due to storage limitations');
        }
        break;

      case 'forward':
        // Add forwarded message to conversation history
        try {
          const savedForward = testConfigManager.addToConversationHistory(data);
          setConversationHistory(prev => [savedForward, ...prev.slice(0, 99)]);

          const allUsers = userProfiles.all || userProfiles;
          const forwardRecipient = Object.values(allUsers).find(user => user.userId === data.recipientId);

          addTestResult('success', `Message forwarded to ${forwardRecipient?.name || 'Unknown User'}`, {
            messageId: data.id,
            forwardedFrom: data.metadata.forwardedFrom,
            recipient: forwardRecipient?.name || data.recipientId
          });
        } catch (error) {
          console.error('Failed to save forwarded message to history:', error);
          addTestResult('warning', 'Message forwarded but not saved to history due to storage limitations');
        }
        break;

      case 'archive':
        // Handle archive action
        addTestResult('success', `Message archived for recipient`, {
          messageId: data.messageId,
          recipientId: data.recipientId,
          action: 'archive'
        });
        break;

      default:
        console.warn('Unknown message action:', action);
        addTestResult('warning', `Unknown message action: ${action}`);
    }
  };

  // SSE integration for message verification
  useEffect(() => {
    if (!isConnected) return;

    const handleSSEMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle message delivery confirmations
        if (data.type === 'MESSAGE_DELIVERED' && verificationData) {
          setMessageDeliveryStatus('delivered');
          addTestResult('info', `Real-time delivery confirmation received for message to ${verificationData.recipient.name}`);
        }

        // Handle message read confirmations
        if (data.type === 'MESSAGE_READ' && verificationData) {
          setMessageDeliveryStatus('read');
          addTestResult('info', `Message read by ${verificationData.recipient.name}`);
        }

        // Handle message failures
        if (data.type === 'MESSAGE_FAILED' && verificationData) {
          setMessageDeliveryStatus('failed');
          addTestResult('error', `Message delivery failed: ${data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.warn('Failed to parse SSE message:', error);
      }
    };

    // Note: In a real implementation, you would add the event listener to the SSE connection
    // For now, this is a placeholder for the SSE integration structure
    console.log('[TestLab] SSE integration ready for message verification');

    return () => {
      // Cleanup SSE listeners
      console.log('[TestLab] Cleaning up SSE message verification listeners');
    };
  }, [isConnected, verificationData]);

  // Add test result
  const addTestResult = (type, message, details = null) => {
    const result = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      message,
      timestamp: new Date().toLocaleTimeString(),
      details
    };
    setTestResults(prev => [result, ...prev.slice(0, 19)]); // Keep last 20 results
  };

  // Test rate calculations
  const testRateCalculations = () => {
    const testCases = [
      {
        name: 'Basic Professional Rate',
        baseRate: 650,
        factors: {},
        expected: 650
      },
      {
        name: 'Complex Fantasy Project',
        baseRate: 650,
        factors: {
          accents: true,
          multipleAgeRanges: true,
          complexEmotional: true,
          exclusivity: true,
          characterCount: 8
        },
        expected: RATE_CALCULATIONS.calculateRate(650, {
          accents: true,
          multipleAgeRanges: true,
          complexEmotional: true,
          exclusivity: true,
          characterCount: 8
        })
      }
    ];

    const results = testCases.map(testCase => ({
      ...testCase,
      actual: RATE_CALCULATIONS.calculateRate(testCase.baseRate, testCase.factors),
      passed: RATE_CALCULATIONS.calculateRate(testCase.baseRate, testCase.factors) === testCase.expected
    }));

    addTestResult('info', 'Rate calculation tests completed', results);
  };

  // Render business scenarios section
  const renderBusinessScenarios = () => {
    const scenarioOptions = availableScenarios.map(scenario => ({
      key: scenario.id,
      value: scenario.id,
      text: scenario.name,
      description: scenario.description
    }));

    return (
      <div className='enhanced-messaging-test-sub-container second-container'>
        <Header as="h3">
          <Icon name="play circle" />
          Business Message Scenarios
          <Header.Subheader>
            Pre-configured realistic business messaging workflows
          </Header.Subheader>
        </Header>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Select Scenario</label>
            <Dropdown
              placeholder="Choose a business scenario..."
              fluid
              selection
              value={testHarnessState.scenarioId}
              options={scenarioOptions}
              onChange={(e, { value }) => setTestHarnessState(prev => ({
                ...prev,
                scenarioId: value,
                phase: 'SELECT_SCENARIO'
              }))}
            />
          </div>
          <div>
            <Button
              onClick={() => runBusinessScenario(testHarnessState.scenarioId)}
              primary
              loading={isLoading}
              disabled={!testHarnessState.scenarioId}
              icon="play"
              content="Run Scenario"
            />
          </div>
          <div>
            <Button
              onClick={() => setShowScenariosModal(true)}
              icon="list"
              content="View All Scenarios"
            />
          </div>
        </div>

        {testHarnessState.scenario && testHarnessState.isScenarioValid && (
          <Message info>
            <Message.Header>Active Scenario: {testHarnessState.scenario.name}</Message.Header>
            <Message.Content>
              <strong>Context:</strong> {testHarnessState.scenario.context}
              <br />
              <strong>Message Type:</strong> {testHarnessState.messageType}
              <br />
              <strong>Current User:</strong> {testHarnessState.currentUser?.name} ({testHarnessState.currentUser?.role})
              {testHarnessState.recipientId && (userProfiles.all || userProfiles)[testHarnessState.recipientId] && (
                <>
                  <br />
                  <strong>Recipient:</strong> {(userProfiles.all || userProfiles)[testHarnessState.recipientId].name} ({(userProfiles.all || userProfiles)[testHarnessState.recipientId].role})
                </>
              )}
              {(testHarnessState.participants.voiceActorId || testHarnessState.participants.bookCreatorId || (testHarnessState.participants.isAdminOverride && testHarnessState.participants.adminId)) && (
                <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#0066cc' }}>
                  <Icon name="check circle" />
                  Using selected Test Participants instead of scenario defaults
                </div>
              )}
              {testHarnessState.opportunityId && opportunities[testHarnessState.opportunityId] && (
                <>
                  <br />
                  <strong>Related Opportunity:</strong> {opportunities[testHarnessState.opportunityId].title}
                </>
              )}
            </Message.Content>
          </Message>
        )}

        {testHarnessState.validationMessage && !testHarnessState.isScenarioValid && (
          <Message warning>
            <Message.Header>Scenario Validation</Message.Header>
            <Message.Content>{testHarnessState.validationMessage}</Message.Content>
          </Message>
        )}
      </div>
    );
  };

  // Render scenarios modal
  const renderScenariosModal = () => (
    <Modal open={showScenariosModal} onClose={() => setShowScenariosModal(false)} size="large">
      <Modal.Header>Business Message Scenarios</Modal.Header>
      <Modal.Content scrolling>
        <Card.Group>
          {availableScenarios.map(scenario => (
            <Card key={scenario.id}>
              <Card.Content>
                <Card.Header>
                  <Icon name="play circle" />
                  {scenario.name}
                </Card.Header>
                <Card.Meta>
                  {scenario.scenario?.sender || 'Sender'} → {scenario.scenario?.recipient || 'Recipient'}
                </Card.Meta>
                <Card.Description>
                  <p>{scenario.description}</p>
                  <p><strong>Context:</strong> {scenario.scenario?.context || 'N/A'}</p>
                  <p><strong>Message Type:</strong> {scenario.messageType}</p>
                </Card.Description>
              </Card.Content>
              <Card.Content extra>
                <Button
                  onClick={() => {
                    setTestHarnessState(prev => ({
                      ...prev,
                      scenarioId: scenario.id
                    }));
                    setShowScenariosModal(false);
                    runBusinessScenario(scenario.id);
                  }}
                  primary
                  size="small"
                  icon="play"
                  content="Load This Scenario"
                />
              </Card.Content>
            </Card>
          ))}
        </Card.Group>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setShowScenariosModal(false)}>Close</Button>
      </Modal.Actions>
    </Modal>
  );

  // Get available message types for current user
  const getAvailableMessageTypesForUser = () => {
    if (!testHarnessState.currentUser) return [];

    return Object.entries(businessMessageConfig).filter(([messageType, config]) => {
      const userRoleUpper = testHarnessState.currentUser.role.toUpperCase();
      return config.senderRoles.includes(userRoleUpper) || testHarnessState.currentUser.role === 'ADMIN';
    }).map(([messageType, config]) => ({
      key: messageType,
      value: messageType,
      text: config.name,
      icon: config.icon
    }));
  };


  // Render message composer
  const renderMessageComposer = () => {
    const availableTypes = getAvailableMessageTypesForUser();
    const opportunityOptions = Object.entries(opportunities).map(([id, opp]) => ({
      key: id,
      value: id,
      text: opp.title
    }));

    return (
      <div className='enhanced-messaging-test-sub-container third-container'>
        <Header as="h3">
          <Icon name="edit" />
          Message Composer
        </Header>

        <div>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Message Type</label>
              <Dropdown
                placeholder="Select message type..."
                fluid
                selection
                value={testHarnessState.messageType}
                options={availableTypes}
                onChange={(e, { value }) => setTestHarnessState(prev => ({
                  ...prev,
                  messageType: value
                }))}
                disabled={!testHarnessState.currentUser}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Recipient</label>
              <Dropdown
                placeholder="Select recipient..."
                fluid
                selection
                value={testHarnessState.recipientId}
                options={Object.entries(userProfiles.all || userProfiles)
                  .filter(([userKey, profile]) => profile && profile.name && profile.role && profile.userId)
                  .map(([userKey, profile]) => ({
                    key: userKey,
                    value: profile.userId,
                    text: `${profile.name} (${profile.role})`,
                    icon: 'user'
                  }))
                }
                onChange={(e, { value }) => setTestHarnessState(prev => ({
                  ...prev,
                  recipientId: value
                }))}
                disabled={!testHarnessState.messageType}
              />
            </div>
          </div>

          {testHarnessState.messageType && (
            <div style={{ position: 'relative', display: 'flex', gap: '15px', marginBottom: '15px', zIndex: "1" }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Related Opportunity (Optional)</label>
                <Dropdown
                  placeholder="Select opportunity..."
                  fluid
                  selection
                  clearable
                  value={testHarnessState.opportunityId}
                  options={opportunityOptions}
                  onChange={(e, { value }) => setTestHarnessState(prev => ({
                    ...prev,
                    opportunityId: value
                  }))}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Message Template</label>
                <Button.Group fluid>
                  <Button
                    type="button"
                    onClick={() => {
                      const content = generateMessageContent();
                      setTestHarnessState(prev => ({
                        ...prev,
                        messageContent: content
                      }));
                    }}
                    disabled={!testHarnessState.messageType || !testHarnessState.currentUser}
                    icon="magic"
                    content="Generate Realistic Content"
                  />
                  <Button
                    type="button"
                    onClick={() => setTestHarnessState(prev => ({
                      ...prev,
                      messageContent: ''
                    }))}
                    disabled={!testHarnessState.messageContent.trim()}
                    icon="eraser"
                    content="Clear"
                  />
                </Button.Group>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Message Content</label>
            <TextArea
              placeholder="Enter message content or use 'Generate Realistic Content' button..."
              value={testHarnessState.messageContent}
              onChange={(e, { value }) => setTestHarnessState(prev => ({
                ...prev,
                messageContent: value
              }))}
              rows={8}
              style={{ width: '100%', minHeight: '200px' }}
            />
          </div>

          <Button
            onClick={handleSendTestMessage}
            primary
            loading={isLoading}
            disabled={!testHarnessState.messageType || !testHarnessState.recipientId || !testHarnessState.currentUser || !testHarnessState.messageContent.trim()}
            icon="send"
            content="Send Test Message"
          />
        </div>
      </div>
    );
  };

  // Render conversation history
  const renderConversationHistory = () => (
    <div className='fifth-container' style={{ padding: '14px', marginBottom: '14px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', backgroundColor: '#fff' }}>
      <Header as="h3">
        <Icon name="history" />
        Recent Test Messages
        <Button
          floated="right"
          size="mini"
          onClick={() => {
            testConfigManager.clearConversationHistory();
            setConversationHistory([]);
            addTestResult('info', 'Conversation history cleared');
          }}
          icon="trash"
          content="Clear History"
        />
      </Header>

      {conversationHistory.length === 0 ? (
        <Message info>No test messages sent yet.</Message>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {conversationHistory.slice(0, 10).map((msg, index) => (
            <Card key={`${msg.id}-${index}`} fluid>
              <Card.Content>
                <Card.Header>
                  <Icon name={businessMessageConfig[msg.messageType]?.icon || 'mail'} />
                  {businessMessageConfig[msg.messageType]?.name || msg.messageType}
                  <Label size="mini" color="blue" floated="right">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </Label>
                </Card.Header>
                <Card.Meta>
                  From: {(userProfiles.all || userProfiles)[msg.senderId]?.name || msg.senderId} →
                  To: {(userProfiles.all || userProfiles)[msg.recipientId]?.name || msg.recipientId}
                </Card.Meta>
                <Card.Description>
                  <div style={{
                    maxHeight: '100px',
                    overflowY: 'auto',
                    fontSize: '0.9em',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {msg.content.substring(0, 200)}
                    {msg.content.length > 200 && '...'}
                  </div>
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render statistics
  const renderStatistics = () => {
    const stats = testConfigManager.getStatistics();

    return (
      <Modal open={showStatsModal} onClose={() => setShowStatsModal(false)}>
        <Modal.Header>Test Statistics</Modal.Header>
        <Modal.Content>
          <Statistic.Group widths="four">
            <Statistic>
              <Statistic.Value>{stats.totalMessages}</Statistic.Value>
              <Statistic.Label>Total Messages</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{stats.totalProfiles}</Statistic.Value>
              <Statistic.Label>User Profiles</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{Object.keys(stats.messagesByType).length}</Statistic.Value>
              <Statistic.Label>Message Types Used</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>{Object.keys(opportunities).length}</Statistic.Value>
              <Statistic.Label>Opportunities</Statistic.Label>
            </Statistic>
          </Statistic.Group>

          <Divider />

          <Header as="h4">Messages by Type</Header>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Message Type</Table.HeaderCell>
                <Table.HeaderCell>Count</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(stats.messagesByType).map(([type, count]) => (
                <Table.Row key={type}>
                  <Table.Cell>{businessMessageConfig[type]?.name || type}</Table.Cell>
                  <Table.Cell>{count}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowStatsModal(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  };

  // Main render
  return (
    <Container className="enhanced-messaging-test" style={{ padding: '20px' }}>
      <Header as="h1" icon textAlign="center">
        <Icon name="lab" />
        Enhanced Universal Messaging Test Lab
        <Header.Subheader>
          Test realistic business message transactions with persistent configuration
        </Header.Subheader>
      </Header>

      {/* Control Panel */}
      <div className='enhanced-messaging-test-sub-container'>
        <Header as="h3">Control Panel</Header>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button.Group>
            <Button
              icon="user"
              content="User Profiles"
              onClick={() => setShowConfigModal(true)}
            />
            <Button
              icon="chart bar"
              content="Statistics"
              onClick={() => setShowStatsModal(true)}
            />
            <Button
              icon="calculator"
              content="Test Rate Calculations"
              onClick={testRateCalculations}
            />
          </Button.Group>

          <Button.Group>
            <Button
              icon="download"
              content="Export Config"
              onClick={() => testConfigManager.exportConfig()}
            />
            <Button
              icon="refresh"
              content="Reset to Defaults"
              onClick={() => {
                testConfigManager.resetToDefaults();
                loadConfiguration();
                addTestResult('info', 'Configuration reset to defaults');
              }}
              color="orange"
            />
          </Button.Group>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Label>
              <Icon name={useRealData ? 'database' : 'lab'} />
              {useRealData ? 'Real Data' : 'Mock Data'}
            </Label>
            <Button
              toggle
              active={useRealData}
              onClick={handleToggleRealData}
              loading={isLoading}
              icon={useRealData ? 'database' : 'lab'}
              content={useRealData ? 'Switch to Mock' : 'Switch to Real'}
              color={useRealData ? 'green' : 'blue'}
            />
          </div>
        </div>
      </div>



      {/* New Architecture User Selection */}
      <div className='enhanced-messaging-test-sub-container first-container'>
        <Header as="h3">
          <Icon name="users" />
          Test Participants Selection
          <Header.Subheader>
            Select Voice Actor and Book Creator for testing scenarios, or use Admin Override
          </Header.Subheader>
        </Header>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
          <VoiceActorSelector
            voiceActors={voiceActorProfiles}
            selectedVoiceActor={testHarnessState.participants.voiceActorId}
            onVoiceActorChange={handleVoiceActorChange}
            isLoading={isLoadingRealData}
            useRealData={useRealData}
            disabled={testHarnessState.participants.isAdminOverride}
          />

          <BookCreatorSelector
            bookCreators={bookCreatorProfiles}
            selectedBookCreator={testHarnessState.participants.bookCreatorId}
            onBookCreatorChange={handleBookCreatorChange}
            isLoading={isLoadingRealData}
            useRealData={useRealData}
            disabled={testHarnessState.participants.isAdminOverride}
          />

          <AdminOverrideSelector
            admins={adminProfiles}
            selectedAdmin={testHarnessState.participants.adminId}
            onAdminChange={handleAdminChange}
            isOverrideActive={testHarnessState.participants.isAdminOverride}
            onToggleOverride={handleToggleAdminOverride}
            isLoading={isLoadingRealData}
            useRealData={useRealData}
          />
        </div>

        {/* Active Test Context Display */}
        <ActiveTestContext
          selectedVoiceActor={testHarnessState.participants.voiceActorId}
          selectedBookCreator={testHarnessState.participants.bookCreatorId}
          selectedAdmin={testHarnessState.participants.adminId}
          isAdminOverride={testHarnessState.participants.isAdminOverride}
          voiceActors={voiceActorProfiles}
          bookCreators={bookCreatorProfiles}
          admins={adminProfiles}
          selectedScenario={testHarnessState.scenarioId}
          businessScenarios={availableScenarios}
        />
      </div>
      {/* Business Scenarios */}
      {renderBusinessScenarios()}
      {/* Message Composer */}
      {testHarnessState.currentUser && renderMessageComposer()}

      {/* System Status */}
      <div className='enhanced-messaging-test-sub-container fourth-container'>
        <Header as="h3">System Status</Header>
        <Grid columns={4} divided>
          <Grid.Column textAlign="center">
            <Label color={isConnected ? 'green' : 'red'} size="large">
              <Icon name={isConnected ? 'wifi' : 'signal'} />
              {isConnected ? 'Connected' : 'Disconnected'}
            </Label>
            <div>SSE Connection</div>
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Label color="blue" size="large">
              <Icon name="user" />
              {testHarnessState.currentUser?.role || 'None'}
            </Label>
            <div>Active User Role</div>
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Label color="orange" size="large">
              <Icon name="mail" />
              {conversationHistory.length}
            </Label>
            <div>Test Messages Sent</div>
          </Grid.Column>
          <Grid.Column textAlign="center">
            <Label color="green" size="large">
              <Icon name="briefcase" />
              {unreadCounts.business}
            </Label>
            <div>Business Messages</div>
          </Grid.Column>
        </Grid>
      </div>

      {/* Conversation History */}
      {renderConversationHistory()}

      {/* Test Results */}
      {testResults.length > 0 && (
        <div style={{ padding: '14px', marginBottom: '14px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', backgroundColor: '#fff' }}>
          <Header as="h3">
            Test Results
            <Button
              floated="right"
              size="mini"
              onClick={() => setTestResults([])}
              icon="trash"
              content="Clear"
            />
          </Header>
          {testResults.slice(0, 5).map(result => (
            <Message
              key={result.id}
              success={result.type === 'success'}
              error={result.type === 'error'}
              warning={result.type === 'warning'}
              info={result.type === 'info'}
            >
              <Message.Header>
                {result.timestamp} - {result.message}
              </Message.Header>
              {result.details && (
                <Message.Content>
                  <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                    {JSON.stringify(result.details, null, 2)}
                  </pre>
                </Message.Content>
              )}
            </Message>
          ))}
        </div>
      )}

      {/* Message Icon Demo */}
      <div style={{ padding: '14px', marginBottom: '14px', border: '1px solid rgba(34,36,38,.15)', borderRadius: '4px', backgroundColor: '#fff' }}>
        <Header as="h3">Universal Message Icon Demo</Header>
        <div style={{ position: 'relative', height: '100px', border: '1px dashed #ccc' }}>
          <UniversalMessageIcon
            position="top-right"
            userRole={testHarnessState.currentUser?.role}
            showBusinessNotifications={true}
          />
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Icon name="arrow right" size="large" />
            <br />
            Message icon appears here →
          </div>
        </div>
      </div>

      {/* Statistics Modal */}
      {renderStatistics()}

      {/* Scenarios Modal */}
      {renderScenariosModal()}

      {/* Configuration Modal */}
      <Modal open={showConfigModal} onClose={() => setShowConfigModal(false)} size="large">
        <Modal.Header>Test Configuration</Modal.Header>
        <Modal.Content scrolling>
          <Tab panes={[
            {
              menuItem: 'User Profiles',
              render: () => (
                <Tab.Pane>
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Role</Table.HeaderCell>
                        <Table.HeaderCell>Experience</Table.HeaderCell>
                        <Table.HeaderCell>Base Rate</Table.HeaderCell>
                        <Table.HeaderCell>Projects</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {Object.values(userProfiles).map(profile => (
                        <Table.Row key={profile.userId}>
                          <Table.Cell>{profile.name}</Table.Cell>
                          <Table.Cell>{profile.role}</Table.Cell>
                          <Table.Cell>{profile.experienceLevel || 'N/A'}</Table.Cell>
                          <Table.Cell>${profile.baseRate || 'N/A'}</Table.Cell>
                          <Table.Cell>{profile.completedProjects || 0}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Tab.Pane>
              )
            },
            {
              menuItem: 'Opportunities',
              render: () => (
                <Tab.Pane>
                  <Card.Group>
                    {Object.values(opportunities).map(opp => (
                      <Card key={opp.id}>
                        <Card.Content>
                          <Card.Header>{opp.title}</Card.Header>
                          <Card.Meta>{opp.genre} • {opp.estimatedFinishedHours} hours</Card.Meta>
                          <Card.Description>
                            <strong>Characters:</strong> {opp.characterRequirements?.totalUniqueVoices || 1}
                            <br />
                            <strong>Complexity:</strong> {opp.characterRequirements?.emotionalRange || 'Basic'}
                            <br />
                            <strong>Budget:</strong> ${opp.compensation?.budgetRange?.min || 'TBD'} - ${opp.compensation?.budgetRange?.max || 'TBD'}
                          </Card.Description>
                        </Card.Content>
                      </Card>
                    ))}
                  </Card.Group>
                </Tab.Pane>
              )
            }
          ]} />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowConfigModal(false)}>Close</Button>
        </Modal.Actions>
      </Modal>

      {/* Message Verification Modal */}
      <MessageVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        messageData={verificationData?.messageData}
        sender={verificationData?.sender}
        recipient={verificationData?.recipient}
        deliveryStatus={messageDeliveryStatus}
        onSimulateNotification={handleSimulateNotification}
        businessMessageConfig={businessMessageConfig}
        useRealData={useRealData}
        onMessageAction={handleMessageAction}
        availableUsers={userProfiles}
      />

      <Divider />

      <Message info>
        <Message.Header>Enhanced Testing Features</Message.Header>
        <Message.List>
          <Message.Item><strong>Business Scenarios:</strong> Pre-configured realistic messaging workflows for common business interactions</Message.Item>
          <Message.Item><strong>Real Data Integration:</strong> Toggle between mock data and real database users/opportunities for testing</Message.Item>
          <Message.Item><strong>User Personas:</strong> Switch between different user roles to test role-based messaging permissions</Message.Item>
          <Message.Item><strong>Smart Content Generation:</strong> Generate realistic message content based on industry pricing models and business context</Message.Item>
          <Message.Item><strong>Auto-fill Logic:</strong> Intelligent recipient selection based on message type and business rules</Message.Item>
          <Message.Item><strong>Persistent Configuration:</strong> Test data and settings saved between sessions for continuous testing</Message.Item>
          <Message.Item><strong>Rate Calculations:</strong> Test complex pricing algorithms with various project factors</Message.Item>
          <Message.Item><strong>Analytics & History:</strong> Comprehensive conversation tracking and usage statistics</Message.Item>
        </Message.List>
      </Message>
    </Container>
  );
};

export default UniversalMessagingTest;
